/**
 * Industries: single source of truth for
 *   - the Explore mega menu (name + tagline)
 *   - the home "Industries" hub
 *   - each Industry page (/industries/:id): SEO meta, theme and hero cut-out
 *
 * Page copy (headline, facts, paths, journey, programme cards) comes from the content writer: src/data/industryContent.js.
 * Page copy lives in src/data/industryContent.js. This file holds nav labels, SEO meta, theme and hero cut-outs only.
 *
 * SEO: `metaTitle` and `metaDescription` are written to be edited
 * by the SEO owner. Keep metaTitle <= ~60 chars and metaDescription <= ~155 chars.
 * Programs themselves come from the backend (see src/lib/programs.js), not from here.
 */

const IMAGES = {
  medical: "https://alearninglab.com/wp-content/uploads/2025/05/Medical-01.webp",
  tech: "https://alearninglab.com/wp-content/uploads/2025/05/VI-01.webp",
  business: "https://alearninglab.com/wp-content/uploads/2025/08/Stanford-004.png",
  law: "https://alearninglab.com/wp-content/uploads/2025/05/Law-01.webp",
};

/**
 * Themes use full Tailwind class literals (required for Tailwind's scanner).
 * Brand rule: only blue and orange (plus tints) are allowed, so industries alternate between the two.
 * Card keys are consumed by ProgramCard; page keys by the Industry page.
 */
const THEMES = {
  blue: {
    accentText: "text-brand-600",
    accentBg: "bg-brand-500 text-white",
    accentSoft: "bg-brand-100 text-ink",
    accentHoverBg: "group-hover:bg-brand-500 group-hover:text-white",
    accentRing: "hover:border-brand-500",
    blob: "brand",
    blobAlt: "sun",
    tint: "bg-brand-100",
    cardTone: "brandTint",
    badge: "bg-brand-500 text-white",
    stage: "border-brand-200 bg-brand-100",
    stagePill: "border-brand-200",
    stagePillIcon: "bg-brand-500 text-white",
    stageSticker: "border-brand-500 bg-brand-500 text-white",
    doCard: "border-brand-200 bg-white text-ink",
    doNumber: "text-brand-300",
    doText: "text-ink/70",
  },
};
// Every industry uses the same calm blue theme (orange is kept for the main call to action).
THEMES.sun = THEMES.blue;

export const INDUSTRIES = [
  {
    id: "health",
    name: "Healthcare & Human Sciences",
    tagline: "Shadow doctors, dentists, therapists",
    icon: "stethoscope",
    theme: THEMES.blue,
    heroImage: IMAGES.medical,
    heroImageAlt: "Students learning clinical skills with a doctor in a virtual healthcare internship",
    cutout: { file: "hero/healthcare.webp", alt: "A teen in a lab coat using a stethoscope on a medical training mannequin with a healthcare instructor" },
    metaTitle: "Healthcare & Human Sciences Internships for Students | A Learning Lab India",
    metaDescription:
      "Explore virtual internships in medicine, dentistry, psychology and healthtech. Shadow doctors and therapists and build real skills. For students in India.",
  },
  {
    id: "ui-ux-creative-designing",
    name: "UI/UX & Creative Designing",
    tagline: "Design apps and exclusive experiences",
    icon: "palette",
    theme: THEMES.blue,
    heroImage: IMAGES.tech,
    heroImageAlt: "Students designing an app interface during a UI/UX virtual internship",
    cutout: { file: "hero/technology.webp", alt: "A teen in a suit reviewing charts and graphs at a table with a mentor" },
    metaTitle: "UI/UX & Creative Design Internships for Students | A Learning Lab India",
    metaDescription:
      "Learn UI/UX, visual identity and creative design in virtual internships for students in India. Design apps and experiences guided by industry designers.",
  },
  {
    id: "gaming-innovation",
    name: "Gaming & Innovation",
    tagline: "Explore games, VR, aviation careers",
    icon: "gamepad",
    theme: THEMES.sun,
    heroImage: IMAGES.tech,
    heroImageAlt: "Students prototyping a game and virtual reality experience in a virtual internship",
    cutout: { file: "hero/aviation.webp", alt: "A teen in a pilot uniform at a flight simulator with an aviation mentor" },
    metaTitle: "Gaming, VR & Innovation Internships for Students | A Learning Lab India",
    metaDescription:
      "Prototype games, VR experiences and explore aviation careers in virtual internships for school students in India. Learn from creators and innovators.",
  },
  {
    id: "finance",
    name: "Finance, Investment & FinTech",
    tagline: "Learn about markets & fintech",
    icon: "chart",
    theme: THEMES.blue,
    heroImage: IMAGES.business,
    heroImageAlt: "Students analysing markets and investments in a finance virtual internship",
    cutout: { file: "hero/technology.webp", alt: "A teen in a suit reviewing charts and graphs at a table with a finance mentor" },
    metaTitle: "Finance, Investment & FinTech Internships for Students | A Learning Lab India",
    metaDescription:
      "Virtual internships in banking, investing, venture capital and fintech for students in India. Learn how money and markets work with finance professionals.",
  },
  {
    id: "business-marketing",
    name: "Business & Marketing",
    tagline: "Build startups, shape brands",
    icon: "briefcase",
    theme: THEMES.sun,
    heroImage: IMAGES.business,
    heroImageAlt: "Students pitching a startup idea in a business and marketing virtual internship",
    cutout: { file: "hero/technology.webp", alt: "A teen in a suit reviewing charts and graphs at a table with a business mentor" },
    metaTitle: "Business & Marketing Internships for Students | A Learning Lab India",
    metaDescription:
      "Build a startup plan and run a brand campaign in virtual business and marketing internships for students in India. Learn from founders and marketers.",
  },
  {
    id: "technology-and-engineering",
    name: "Technology & Engineering",
    tagline: "Step inside AI, engineering",
    icon: "code",
    theme: THEMES.blue,
    heroImage: IMAGES.tech,
    heroImageAlt: "Students exploring technology and engineering in a virtual internship",
    cutout: {
      file: "hero/engineering.webp",
      alt: "A teen in a hard hat working on a robotics bench with an engineering mentor pointing at the equipment",
    },
    metaTitle: "Technology & Engineering Internships for Students | A Learning Lab India",
    metaDescription:
      "Virtual internships in AI, computer science and engineering for students in India. See how code, machines and infrastructure actually get built.",
  },
  {
    id: "law-public-policy-global-affairs",
    name: "Law, Public Policy & Global Affairs",
    tagline: "Practice law, shape policy",
    icon: "scale",
    theme: THEMES.blue,
    heroImage: IMAGES.law,
    heroImageAlt: "Students debating a mock legal case in a law and public policy virtual internship",
    cutout: { file: "hero/education.webp", alt: "A teen in a blazer reviewing documents at a table with a law and policy mentor" },
    metaTitle: "Law, Public Policy & Global Affairs Internships | A Learning Lab India",
    metaDescription:
      "Virtual internships in law, public policy and global affairs for students in India. Draft briefs, debate policy and learn from legal and policy practitioners.",
  },
];

/** Old URLs and ids that must keep working. Maps to the current industry id. */
export const LEGACY_INDUSTRY_IDS = {
  business: "business-marketing",
};

export function resolveIndustryId(id) {
  return LEGACY_INDUSTRY_IDS[id] ?? id;
}

export function getIndustry(id) {
  const resolved = resolveIndustryId(id);
  return INDUSTRIES.find((industry) => industry.id === resolved);
}

export function industryPath(id) {
  return `/industries/${id}`;
}
